/**
 * @file The fetch jobs this browser has been told to stop offering. Consumers:
 * `useSignifyFetchJobProgress`, whose "New list" records one, and
 * `findReattachableSignifyFetchJob`, which skips them.
 * Local to the viewer on purpose: dismissing is a view choice about one operator's own screen, the
 * job document is shared truth that `firestore.rules` lets a client change in only one field, and
 * the job's seven-day TTL reaps it anyway — a round trip and a rules change would buy nothing.
 * Bounded to the most recent ids, newest first, so the list cannot grow without limit; anything
 * older has been reaped long before it falls off. Every access is guarded, because Safari's private
 * mode throws on `localStorage`, and a fetch that cannot be dismissed beats a modal that cannot open.
 */

const STORAGE_KEY = 'signify.fetchJob.dismissed';
const REMEMBERED_LIMIT = 20;

const read = (): string[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === null) return [];
    const parsed: unknown = JSON.parse(stored);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((jobId): jobId is string => typeof jobId === 'string');
  } catch {
    return [];
  }
};

export const dismissedSignifyFetchJobs = {
  has: (jobId: string): boolean => read().includes(jobId),

  add: (jobId: string): void => {
    const kept = [jobId, ...read().filter((held) => held !== jobId)].slice(0, REMEMBERED_LIMIT);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(kept));
    } catch (error) {
      console.warn('[dismissedSignifyFetchJobs] Could not record the dismissal', error);
    }
  },
};
